// flow/components/FlowOverlays.tsx
import { SidebarPresence } from '@/components/nodes/presence/SidebarPresence'
import OverlayTopBar from '@/components/OverlayTopBar/OverlayTopBar'
import OverlayBottomBar from '@/components/OverlayBottomBar/OverlayBottomBar'

interface FlowOverlaysProps {
  // Sidebar presence props
  selectedNodeId?: string
  selectedNodeData?: any
  
  // Top bar props
  zoomLevel: number
  showMap: boolean
  projectId: string
  projectName: string
  projectDescription: string
  userName: string
  cursors: any
  theme: 'light' | 'dark'
  toggleTheme: () => void
  backgroundPreset: string
  backgroundPresets: any[]
  setBackgroundPreset: (preset: string) => void
  onShowFileDialog: () => void
  onShowHelpGuide: () => void
  onShowNodeIndex: () => void
  hasCopiedNode: boolean
  onPasteNode?: () => void
  onShowProjectSettings: () => void
  onShowBlueprints: () => void
  
  // Search props
  isSearchVisible?: boolean
  searchTerm?: string
  searchMatches?: string[]
  currentMatchIndex?: number
  searchInputRef?: React.RefObject<HTMLInputElement>
  onSearchChange?: (value: string) => void
  onSearchKeyDown?: (e: React.KeyboardEvent) => void
  onSearchClose?: () => void
}

export function FlowOverlays({
  selectedNodeId,
  selectedNodeData,
  zoomLevel,
  showMap,
  projectId,
  projectName,
  projectDescription,
  userName,
  cursors,
  theme,
  toggleTheme,
  backgroundPreset,
  backgroundPresets,
  setBackgroundPreset,
  onShowFileDialog,
  onShowHelpGuide,
  onShowNodeIndex,
  hasCopiedNode,
  onPasteNode,
  onShowProjectSettings,
  onShowBlueprints,
  // Search props
  isSearchVisible,
  searchTerm,
  searchMatches,
  currentMatchIndex,
  searchInputRef,
  onSearchChange,
  onSearchKeyDown,
  onSearchClose,
}: FlowOverlaysProps) {
  return (
    <>
      {/* Position SidebarPresence with higher z-index */}
      <SidebarPresence
        nodeId={selectedNodeId}
        nodeData={selectedNodeData}
      />

      {/* Search input overlay */}
      {isSearchVisible && (
        <div className="fixed top-1/4 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 p-4 min-w-[400px]">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search nodes (min 7 characters)..."
                  value={searchTerm || ''}
                  onChange={(e) => onSearchChange?.(e.target.value)}
                  onKeyDown={onSearchKeyDown}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <button
                onClick={onSearchClose}
                className="px-3 py-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>
            {searchMatches && searchMatches.length > 0 && (
              <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {searchMatches.length} match{searchMatches.length !== 1 ? 'es' : ''} found
                {searchMatches.length > 1 && ` (${(currentMatchIndex || 0) + 1}/${searchMatches.length})`}
                <div className="text-xs mt-1 text-gray-500">
                  Press ↓ to cycle, Enter to go, Esc to close
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* OverlayTopBar with new handlers for topbar glassy buttons */}
      {zoomLevel > 0.1 && showMap && (
        <OverlayTopBar
          projectId={projectId}
          projectName={projectName}
          projectDescription={projectDescription}
          userName={userName}
          theme={theme}
          toggleTheme={toggleTheme}
          backgroundPreset={backgroundPreset}
          backgroundPresets={backgroundPresets}
          setBackgroundPreset={setBackgroundPreset}
          onShowFileDialog={onShowFileDialog}
          onShowHelpGuide={onShowHelpGuide}
          onShowNodeIndex={onShowNodeIndex}
          hasCopiedNode={hasCopiedNode}
          onPasteNode={onPasteNode}
          onShowProjectSettings={onShowProjectSettings}
          onShowBlueprints={onShowBlueprints}
        />
      )}      <OverlayBottomBar />
    </>
  )
}

export default FlowOverlays
